/**
 * Network Utilities
 *
 * Centralized utilities for network error detection and connectivity verification.
 * Single source of truth - replaces duplicate implementations across the codebase.
 */

/**
 * Shared configuration for network-related timing values
 * Centralized here for easy tuning and consistency across components
 *
 * Tuning guidelines:
 * - PROBE_TIMEOUT_MS: Higher values tolerate slow networks but delay detection.
 *   3s is a good balance for typical connections.
 * - OFFLINE_PROBE_INTERVAL_MS: Lower = faster recovery detection but more requests.
 *   5s provides quick recovery without excessive polling.
 * - ONLINE_PROBE_INTERVAL_MS: Higher = less battery/bandwidth but slower detection.
 *   30s is conservative for background health checks.
 * - CONSECUTIVE_FAILURES_REQUIRED: Higher = fewer false positives but slower detection.
 *   2 failures filters out transient issues while staying responsive.
 */
export const NETWORK_CONFIG = {
  PROBE_TIMEOUT_MS: 3000,                // Timeout for network probe requests
  OFFLINE_PROBE_INTERVAL_MS: 5000,       // Poll every 5s when offline (fast recovery detection)
  ONLINE_PROBE_INTERVAL_MS: 30000,       // Poll every 30s when online (background check)
  CONSECUTIVE_FAILURES_REQUIRED: 2,      // Require 2 probe failures before showing offline banner
  INITIAL_PROBE_DELAY_ONLINE_MS: 2000,   // Delay before first probe if online on mount
  INITIAL_PROBE_DELAY_OFFLINE_MS: 500,   // Delay before first probe if offline on mount (verify quickly)
}

// Network error detection pattern (matches common fetch/network errors)
export const NETWORK_ERROR_PATTERN = /NetworkError|Failed to fetch|network|fetch|CORS|AbortError/i

/**
 * Check if an error is network-related
 * @param {Error|string|unknown} error - The error to check
 * @returns {boolean} True if the error is network-related
 */
export function isNetworkError(error) {
  if (!error) return false

  const message = typeof error === 'string'
    ? error
    : (error?.message || String(error))

  const name = error?.name || ''

  // Check if error name is AbortError (DOMException from AbortController)
  if (name === 'AbortError') return true

  return NETWORK_ERROR_PATTERN.test(message) ||
    (name === 'TypeError' && /fetch|network/i.test(message))
}

/**
 * Verify network connectivity by making an actual request
 *
 * navigator.onLine is unreliable - it can return true before TCP/DNS is ready
 * (common after wake from sleep). This function makes an actual request to verify.
 *
 * @param {string} supabaseUrl - The Supabase project URL
 * @param {number} timeoutMs - Timeout in milliseconds (default from NETWORK_CONFIG)
 * @param {AbortSignal} externalSignal - Optional external abort signal for cancellation
 * @returns {Promise<boolean>} True if network is ready for requests
 */
export async function probeNetworkReady(supabaseUrl, timeoutMs = NETWORK_CONFIG.PROBE_TIMEOUT_MS, externalSignal = null) {
  // Quick fail if browser reports offline
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return false
  }

  if (!supabaseUrl) {
    console.warn('[NetworkUtils] No supabaseUrl provided for network probe')
    return navigator.onLine // Fall back to navigator.onLine
  }

  // Validate URL format - must be https:// for security (or http:// for localhost dev)
  let isValidUrl = false
  try {
    const url = new URL(supabaseUrl)
    isValidUrl = url.protocol === 'https:' ||
      (url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1'))
  } catch {
    // Invalid URL format
    isValidUrl = false
  }

  if (!isValidUrl) {
    console.error('[NetworkUtils] Invalid supabaseUrl - must be https:// (or http:// for localhost):', supabaseUrl)
    return navigator.onLine // Fall back to navigator.onLine
  }

  // Check if already aborted
  if (externalSignal?.aborted) {
    return false
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  // If external signal aborts, abort our controller too
  const handleExternalAbort = () => controller.abort()
  externalSignal?.addEventListener('abort', handleExternalAbort)

  try {
    // Use Supabase REST endpoint - lightweight HEAD request
    // This endpoint is always available and has CORS configured
    const response = await fetch(`${supabaseUrl}/rest/v1/`, {
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-store',
      headers: {
        // Minimal headers for fastest response
        'Accept': '*/*'
      }
    })

    clearTimeout(timeoutId)
    externalSignal?.removeEventListener('abort', handleExternalAbort)

    // Any response (even 4xx) means network is working
    // Only 5xx or no response indicates server/network issues
    return response.ok || response.status < 500
  } catch (error) {
    clearTimeout(timeoutId)
    externalSignal?.removeEventListener('abort', handleExternalAbort)

    // AbortError means timeout or external cancellation - not a real failure
    // Other errors mean network is definitely not ready
    if (error?.name !== 'AbortError') {
      console.warn('[NetworkUtils] Network probe failed:', error?.message || error)
    }

    return false
  }
}

/**
 * Calculate exponential backoff delay with jitter
 *
 * Jitter prevents "thundering herd" problem when many clients retry simultaneously
 *
 * @param {number} attempt - The current attempt number (0-indexed)
 * @param {number} baseMs - Base delay in milliseconds (default 1000)
 * @param {number} maxMs - Maximum delay in milliseconds (default 30000)
 * @returns {number} Delay in milliseconds
 */
export function getBackoffDelay(attempt, baseMs = 1000, maxMs = 30000) {
  const exponential = baseMs * Math.pow(2, attempt)
  const jitter = Math.random() * 1000 // 0-1 second random jitter
  return Math.min(exponential + jitter, maxMs)
}

/**
 * Wait for a specified duration
 * @param {number} ms - Duration in milliseconds
 * @returns {Promise<void>}
 */
export function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}
